import { z } from 'zod'

// Roles and permissions (issue #71). A permission is `<area>:<action>`. Two roles are built in and always exist:
// Admin (everything) and Editor (all content, no settings, users or mailbox). Advanced and Store sites can define custom roles.

export const AREAS = ['pages', 'articles', 'catalogue', 'media', 'inquiries', 'orders', 'mailbox', 'navigation', 'settings', 'users'] as const
export type Area = (typeof AREAS)[number]

export const ACTIONS = ['view', 'edit', 'publish', 'delete'] as const
export type Action = (typeof ACTIONS)[number]

/** Areas without a publish step: publish is not offered for them. */
const NO_PUBLISH: readonly Area[] = ['media', 'inquiries', 'orders', 'mailbox', 'navigation', 'settings', 'users']

export const permissionKey = (area: Area, action: Action) => `${area}:${action}` as const
export type Permission = ReturnType<typeof permissionKey>

/** Every permission that exists (publish only where it means something). */
export const ALL_PERMISSIONS: readonly Permission[] = AREAS.flatMap((a) => ACTIONS.filter((x) => x !== 'publish' || !NO_PUBLISH.includes(a)).map((x) => permissionKey(a, x)))

const isPermission = (value: string): value is Permission => (ALL_PERMISSIONS as readonly string[]).includes(value)

/** Areas the built-in Editor role does not get: the inbox holds private mail, so only admins and custom roles that grant it see it. */
const NOT_EDITOR: readonly Area[] = ['settings', 'users', 'mailbox']

/** Editor: all content and navigation, nothing about settings, users or the mailbox. */
export const EDITOR_PERMISSIONS: readonly Permission[] = ALL_PERMISSIONS.filter((p) => !NOT_EDITOR.some((a) => p.startsWith(`${a}:`)))

export const PermissionListSchema = z
  .array(z.string())
  .max(ALL_PERMISSIONS.length)
  .transform((list) => [...new Set(list)].filter(isPermission))

export const RoleRecordSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(60),
  description: z.string().max(200),
  permissions: PermissionListSchema,
  /** How many users have this role. */
  users: z.number().int(),
})
export type RoleRecord = z.infer<typeof RoleRecordSchema>
export const RoleListSchema = z.object({ items: z.array(RoleRecordSchema) })

export const RoleInputSchema = z.object({
  name: z.string().trim().min(1).max(60),
  description: z.string().trim().max(200).default(''),
  permissions: PermissionListSchema,
})
export type RoleInput = z.infer<typeof RoleInputSchema>

/**
 * What a user may do. `role` is the built-in role (custom-role users are `editor` underneath); `customPermissions` is
 * set only when the user has a custom role and the site's plan still allows custom roles.
 */
export function effectivePermissions(role: 'admin' | 'editor', customPermissions: readonly string[] | null | undefined): Permission[] {
  if (role === 'admin') return [...ALL_PERMISSIONS]
  if (customPermissions) return customPermissions.filter(isPermission)
  return [...EDITOR_PERMISSIONS]
}

export const hasPermission = (permissions: readonly string[], area: Area, action: Action) => permissions.includes(permissionKey(area, action))

/**
 * Consistency of a permission set: editing or publishing or deleting something needs to be able to see it.
 * Returns the set with `view` added wherever a stronger permission needs it.
 */
export function withImpliedView(permissions: readonly string[]): Permission[] {
  const out = new Set<string>(permissions)
  for (const p of permissions) {
    const [area] = p.split(':')
    if (area && AREAS.includes(area as Area)) out.add(`${area}:view`)
  }
  return [...out].filter(isPermission)
}
